/**
 * Floating sidebar panels — Synth VR only (SRVR-0358).
 *
 * A sidebar box pulled out of its sidebar floats over the editor at a saved
 * position. Kept apart from the shared panel layout (`synapse:panel-layout`,
 * which Console reads too): a floated box keeps its sidebar slot there, which is
 * what lets the dock icon put it back where it was, and Console never sees this key.
 */

import { create } from 'zustand';

export interface FloatPos { x: number; y: number; w: number; }
export interface Rect { left: number; top: number; right: number; bottom: number; }

export const FLOATING_PANELS_KEY = 'synapse:srvr-floating-panels';
/** How close (px) an edge must come to another before it sticks to it. */
export const SNAP_PX = 12;
/** At least this much of a floating box stays on-screen, so it can always be grabbed back. */
const KEEP_VISIBLE_PX = 24;

function load(): Record<string, FloatPos> {
  try {
    const raw = localStorage.getItem(FLOATING_PANELS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed as Record<string, FloatPos>;
    }
  } catch { /* ignore */ }
  return {};
}

function persist(panels: Record<string, FloatPos>): void {
  try { localStorage.setItem(FLOATING_PANELS_KEY, JSON.stringify(panels)); } catch { /* quota */ }
}

interface FloatingPanelStore {
  /** panelId -> position; a panel absent here is docked in its sidebar. */
  panels: Record<string, FloatPos>;
  /** Float a panel (or move a floating one) and save it. */
  float: (id: string, pos: FloatPos) => void;
  /** Move a floating panel without saving — called per pointer move; `save` ends the drag. */
  move: (id: string, x: number, y: number) => void;
  save: () => void;
  /** Return a panel to its sidebar slot. */
  dock: (id: string) => void;
}

export const useFloatingPanels = create<FloatingPanelStore>((set, get) => ({
  panels: load(),
  float: (id, pos) => set(s => {
    const panels = { ...s.panels, [id]: pos };
    persist(panels);
    return { panels };
  }),
  move: (id, x, y) => set(s => {
    const cur = s.panels[id];
    if (!cur) return s;
    return { panels: { ...s.panels, [id]: { ...cur, x, y } } };
  }),
  save: () => persist(get().panels),
  dock: (id) => set(s => {
    if (!s.panels[id]) return s;
    const panels = { ...s.panels };
    delete panels[id];
    persist(panels);
    return { panels };
  }),
}));

/** Keep a box of width `w` reachable inside a `vw`×`vh` window. */
export function clampToViewport(x: number, y: number, w: number, vw: number, vh: number): { x: number; y: number } {
  return {
    x: Math.max(KEEP_VISIBLE_PX - w, Math.min(vw - KEEP_VISIBLE_PX, x)),
    y: Math.max(0, Math.min(vh - KEEP_VISIBLE_PX, y)),
  };
}

/** Smallest shift within `threshold` that lands one of `edges` on one of `lines`, or 0. */
function nearestShift(edges: number[], lines: number[], threshold: number): number {
  let best = 0, bestAbs = threshold + 1;
  for (const e of edges) for (const l of lines) {
    const d = l - e;
    if (Math.abs(d) < bestAbs) { best = d; bestAbs = Math.abs(d); }
  }
  return bestAbs <= threshold ? best : 0;
}

/**
 * The magnet. Shifts `rect` so an edge within `threshold` of another box's edge,
 * or of the window's, sits exactly on it — side by side, stacked, or flush. A box
 * only attracts on an axis when the two overlap (within the threshold) on the
 * other one, so a box far above does not pull a box sideways.
 */
export function snapRect(rect: Rect, others: Rect[], vw: number, vh: number, threshold = SNAP_PX): Rect {
  const xLines: number[] = [0, vw];
  const yLines: number[] = [0, vh];
  for (const o of others) {
    if (rect.top < o.bottom + threshold && rect.bottom > o.top - threshold) xLines.push(o.left, o.right);
    if (rect.left < o.right + threshold && rect.right > o.left - threshold) yLines.push(o.top, o.bottom);
  }
  const dx = nearestShift([rect.left, rect.right], xLines, threshold);
  const dy = nearestShift([rect.top, rect.bottom], yLines, threshold);
  return { left: rect.left + dx, right: rect.right + dx, top: rect.top + dy, bottom: rect.bottom + dy };
}
